import TableHead from "./tableHead/TableHead";
import "./table.css";
import React, { useEffect, useState } from "react";
import TableContent from "./TableContent";
import useUserContext from "../../hooks/useUserContext";
import {FilterProps, TableProps} from "./tableTypes";
import Skeleton from "../skeleton/Skeleton";
import FiltersPanel from "./panel/FiltersPanel";
import useTableData from "./tableContext/useTableData";
import ColumnsPanel from "./panel/ColumnsPanel";
import useAppContext from "../../hooks/useAppContext";
import EmptyState from "../emptyState/EmptyState";
import Pagination from "./pagination/Pagination";

interface TableInnerProps extends TableProps {
    initialFilters?: FilterProps[];
    onRowClick?: (row: any) => void;
}

const TableInner = ({ fetch, cols, title, showSearch, initialFilters, doneLoadingDependencies, filterLocally, onRowClick }: TableInnerProps) => {
    const { selectedDomain } = useUserContext();
    const { data, setData, locallyFilteredData, setLocallyFilteredData, locallyFilteredTotal, setLocallyFilteredTotal, filters, setFilters, filtersBase, setFiltersBase, sortingBy, sortingDirection, isFiltersPanelOpen, isColumnsPanelOpen, isLoading, setIsLoading, total, setTotal, page, setPage, pageSize } = useTableData();

    const assignBaseOptionsToFilters = (): FilterProps[] => {
        return filters.map(filter => {
            if(filter.options) {
                const filterBase = filtersBase.find(fb => fb.filterKey === filter.filterKey);
                return {...filter, options: filterBase?.options || []}
            } else {
                return filter;
            }
        })
    }

    const fetchData = async () => { 
        if(doneLoadingDependencies) {
            if (selectedDomain) {
                setIsLoading(true);
                const response = await fetch({
                    page,
                    pageSize,
                    selectedDomain,
                    filters: assignBaseOptionsToFilters(),
                    sortingBy,
                    sortingDirection
                });
                setData(response.data.report);
                setTotal(response.data.total);
                setLocallyFilteredData(response.data.report);
                setLocallyFilteredTotal(response.data.total);
                setIsLoading(false);
            }
        }
    }

    const exportToExcel = () => {
        if(selectedDomain) {
            fetch({
                selectedDomain,
                filters: assignBaseOptionsToFilters(),
                sortingBy,
                sortingDirection,
                isCsvCall: true,
            });
        }
    }

    const onPaginating = (newPage: number) => {
        if(newPage > page) {
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
        setPage(newPage);
    }

    useEffect(() => {
        if(doneLoadingDependencies) {
            fetchData();
        }
    }, [sortingBy, sortingDirection, selectedDomain, doneLoadingDependencies, page]);

    useEffect(() => {
        if(doneLoadingDependencies) {
            if(filterLocally) {
                const filteredResponse: any[] = filterLocally(data, filters);
                setLocallyFilteredData(filteredResponse);
                setLocallyFilteredTotal(filteredResponse.length);
            } else {
                setPage(0);
                fetchData();
            }
        }
    }, [filters]);

    useEffect(() => {
        setFiltersBase(initialFilters);
    }, [initialFilters]);

    return (
        <div className="table-container">
            {title && <div className="title">{title}</div>}
            <TableHead cols={cols} title={title} showSearch={showSearch} exportToExcel={exportToExcel} />
            {isLoading ? <Skeleton type="table" /> :
                <div className="table-inner">
                    {data?.length ? <div className="scroll-container">
                        <TableContent data={filterLocally ? locallyFilteredData : data} cols={cols} reFetch={fetchData} onRowClick={onRowClick} />
                    </div> : <div className="table-empty-state"><EmptyState subTitle="" /></div>}
                </div>
            }
            {filters?.length ? <FiltersPanel filters={[]} isOpen={isFiltersPanelOpen} /> : null}
            <ColumnsPanel columns={[]} isOpen={isColumnsPanelOpen} />
            <Pagination total={total} pageSize={pageSize} page={page} onPaginating={onPaginating} />
        </div>
    );
}

export default TableInner;